import { Button, Grid, Box, Typography, IconButton, Stack } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { deleteItem, getRentalItems } from '../../../redux/slices/rentals';
import { useDispatch, useSelector } from '../../../redux/store';
import { ConfirmDialog } from '../../../reusable-components/confirm-dialog';
import CustomDataGrid from '../../../reusable-components/datagrid/CustomDataGrid';
import { RENTAL_CATALOG } from '../../../reusable-components/datagrid/rentalColumns';
import EditRentalItem from './EditRentalItem';
import Iconify from '../../../reusable-components/iconify';
import { useSnackbar } from '../../../reusable-components/snackbar';
import AddRentalItem from './AddRentalItem';
import { HOST_API_URL } from '../../../config-global';
import { useScopeCheck, ScopeGuard } from '../../../reusable-components/scopes';

export default function RentalCatalog() {
  const dispatch = useDispatch();
  const {
    data: { items },
  } = useSelector((state) => state.rentals);
  const { enqueueSnackbar } = useSnackbar();
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [values, setValues] = useState({});
  const canEditCatalog = useScopeCheck(['Rental-Catalog-Edit'], true);
  const CustomLeftToolbar =  () => ( <CustomToolbar setOpen={setOpen}/> )
      
  const rowClick = (row) => {
    setValues({
      id: row.itemId,
      description: row.description,
      notes: row.notes,
      uomType: row.uomType,
      uomAmount: row.uomAmount,
      capAmount: row.capAmount,
      daysToCap: row.daysToCap,
      vendorId: row.vendorId,
      image: row.image,
      pricingTypes: row.pricingTypes || [],
      pricingTypeId: row.pricingTypeId,
      pricingTypeName: row.pricingTypeName,
      price: row.price,
      weightLimit: row.weightLimit,
      warranty: row.warranty,
      vendorItemLink: row.vendorUrl,
      category: {categoryId: row.categoryId, categoryName: row.categoryName},
      subcategory: {subcategoryId: row.subcategoryId, subcategoryName: row.subcategoryName},});
    setIsEditing(true)
  }; 
  const ActionButtons = ({ row }) => {
    const { itemId, description, notes } = row;
    const [openNote, setOpenNote] = useState({});
    const handleOpenNote = (event, id) => {
      event.openNote = true;
      setOpenNote((prev) => { return({ ...prev, [id]: true })});
    };

    const handleCloseNote = (id) => {
      setOpenNote((prev) => ({ ...prev, [id]: false }));
    };

    return (
      <>
      <ScopeGuard scopes = {['Rental-Catalog-Edit']} allowAdmin>
        <ConfirmDialog
          icon="delete-outline"
          title="Delete"
          color="error"
          content={`Are you sure you want to delete ${description}?`}
          actionButton="Delete"
          action={async () => {
            await dispatch(deleteItem(itemId, enqueueSnackbar));
            dispatch(getRentalItems());
          }}
          tooltip="Delete"
        />
        </ScopeGuard>
        {notes && (
          <Box position="relative" display="inline-block">
            <IconButton color="secondary" onClick={(event) => handleOpenNote( event, itemId)}>
              <Iconify icon="hugeicons:note-02" />
            </IconButton>
            {openNote && openNote[itemId] && (
              <ClickAwayListener onClickAway={() => handleCloseNote(itemId)}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '40px',
                    left: '-90px',
                    zIndex: 10,
                    bgcolor: '#fff',
                    borderRadius: '6px',
                    boxShadow: 'rgba(0, 0, 0, 0.16) 0px 3px 6px',
                    border: '1px solid rgb(193, 201, 208)',
                    padding: '8px',
                    width: '200px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Grid container direction="column" spacing={2}>
                    <Grid item>
                      <Typography color="secondary" fontWeight="bold">
                        Notes
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography>{notes}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </ClickAwayListener>
            )}
          </Box>
        )}
      </>
    );
  };

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    width: 180,
    renderCell: (params) => ActionButtons(params),
    id: 'actions',
    className: 'actions',
  };
  const columns = useMemo(() => [...RENTAL_CATALOG, actions]);

  useEffect(() => {
    dispatch(getRentalItems());
  }, [dispatch]);

  useEffect(() => {
    if (items?.length > 0) setData(items);
  }, [items]);

  return (
    <>
      <Helmet>
        <title>Rentals Catalog | Platform Solutions</title>
      </Helmet>

      {isEditing && <EditRentalItem setIsEditing={setIsEditing} isEditing={setIsEditing} values={values} setValues={setValues} />}
      <AddRentalItem open={open} setOpen={setOpen} />
      <Grid item xs={selected.length > 0 ? 10 : 12}>
        <CustomDataGrid
          boxSX={{ height: 'calc(100vh - 235px)' }}
          checkboxSelection={selected.length > 0}
          CustomLeftToolbar={CustomLeftToolbar}
          data={data}
          displayCheckboxRight
          getRowId={(row) => row.itemId}
          gridColumns={columns}
          gridId="admin-rental-catalog"
          keepNonExistentRowsSelected
          onRowSelectionModelChange={(newSelection) => {
            setSelected(newSelection);
          }}
          onRowClick={(params, e) => {
            const selection = window.getSelection().toString();
            if (selection.length > 0 || !canEditCatalog) return;
            if ( e.openNote || e.clickedImage) return;
            rowClick(params.row);
          }}
          rowSelectionModel={selected}
          sort={[{ field: 'description', sort: 'asc' }]}
        />
      </Grid>
    </>
  );
}

const CustomToolbar = ({setOpen}) => {
   const downloadCatalog = async () => {
      window.open(`${HOST_API_URL}Api/Rental/DownloadRentalCatalog`, '_blank');

      
    };

    return(
      <Stack direction = {'row'}>
        <ScopeGuard scopes = {['Rental-Catalog-Edit']} allowAdmin> 
          <Button
                variant="contained"
                size="small"
                color="secondary"
                onClick={() => {
                  setOpen(true);
                }}
                startIcon={<Iconify icon="eva:plus-outline" />}
                sx={{ mr: 2 }}
              >
                Add Item
            </Button>
          </ScopeGuard>
          <Button
            variant="outlined"
            sx={{ mr: 1 }}
            size="small"
            color="secondary"
            onClick={downloadCatalog}
            startIcon={<Iconify icon="file-icons:microsoft-excel" />}
          >
            Download
          </Button>
        </Stack>
    )
}
     
  